import { useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { useTour } from './tour-provider';
import { TOUR_STEPS } from './tour-steps';

interface Position {
  top: number;
  left: number;
}

export function TourCard() {
  const { active, stepIndex, totalSteps, nextStep, endTour } = useTour();
  const [, navigate] = useLocation();
  const [position, setPosition] = useState<Position>({
    top: 0,
    left: 0,
  });

  const step = TOUR_STEPS[stepIndex];

  // Effect to position the card and manage the tour-highlight class
  // Hook must be placed before the conditional guard
  useLayoutEffect(() => {
    // Guard inside the effect: only run when active and step is defined
    if (!active || !step) {
      return;
    }

    const target = step.target ? document.querySelector(`[data-tour="${step.target}"]`) : null;

    // Add highlight to current target
    if (target) {
      target.classList.add('tour-highlight');
    }

    // Function to reposition the card
    const updatePosition = () => {
      if (target) {
        const rect = target.getBoundingClientRect();
        const cardWidth = 320;
        const cardHeight = 240; // estimate based on content

        let top = rect.bottom + 12 + window.scrollY;
        let left = rect.left + window.scrollX;

        // Flip above if too close to bottom
        if (rect.bottom + cardHeight + 12 > window.innerHeight) {
          top = rect.top - cardHeight - 12 + window.scrollY;
        }

        // Clamp to viewport edges with 16px padding
        left = Math.max(16, Math.min(left, window.innerWidth - cardWidth - 16));

        setPosition({ top, left });
      } else {
        // Fallback: centre in viewport
        setPosition({
          top: window.innerHeight / 2 - 100 + window.scrollY,
          left: window.innerWidth / 2 - 160,
        });
      }
    };

    // Initial position
    updatePosition();

    // Listen for resize and scroll events
    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(document.body);

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    // Cleanup: remove highlight and event listeners
    return () => {
      if (target) {
        target.classList.remove('tour-highlight');
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [active, step]);

  // Get the current step — guard against undefined with noUncheckedIndexedAccess
  if (!active || !step) {
    return null;
  }

  const cardStyle: React.CSSProperties = {
    position: 'absolute',
    top: position.top,
    left: position.left,
    width: 320,
    backgroundColor: '#ffffff',
    borderTop: '3px solid #6366f1',
    borderRadius: '8px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
    zIndex: 9999,
    padding: '16px',
  };

  const card = (
    <div style={cardStyle}>
      {/* Step counter + close button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: '#6366f1',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Step {stepIndex + 1} of {totalSteps}
        </span>
        <button
          type="button"
          onClick={endTour}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#9ca3af',
            fontSize: 18,
            lineHeight: 1,
            padding: '0 2px',
          }}
          aria-label="Close tour"
        >
          ✕
        </button>
      </div>

      {/* Title */}
      <h3
        style={{
          margin: '0 0 8px',
          fontSize: 15,
          fontWeight: 700,
          color: '#111827',
        }}
      >
        {step.title}
      </h3>

      {/* Body */}
      <p
        style={{
          margin: '0 0 16px',
          fontSize: 13,
          color: '#4b5563',
          lineHeight: 1.6,
        }}
      >
        {step.body}
      </p>

      {/* Actions */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        {/* Skip button */}
        <button
          type="button"
          onClick={endTour}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 13,
            color: '#9ca3af',
            padding: '6px 8px',
          }}
        >
          Skip tour
        </button>

        {/* For setup steps: show CTA button + "I've done this" */}
        {step.type === 'setup' && step.ctaPath && (() => {
          const path = step.ctaPath;
          return (
            <button
              type="button"
              onClick={() => {
                if (path) {
                  navigate(path);
                  nextStep();
                }
              }}
              style={{
                background: '#6366f1',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {step.ctaLabel ?? 'Go →'}
            </button>
          );
        })()}

        {/* Next / I've done this */}
        <button
          type="button"
          onClick={nextStep}
          style={{
            background: step.type === 'setup' ? 'none' : '#6366f1',
            color: step.type === 'setup' ? '#6366f1' : '#fff',
            border: step.type === 'setup' ? '1px solid #6366f1' : 'none',
            borderRadius: 6,
            padding: '6px 14px',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {step.type === 'setup' ? "I've done this →" : stepIndex >= totalSteps - 1 ? 'Finish' : 'Next →'}
        </button>
      </div>
    </div>
  );

  return createPortal(card, document.body);
}
